import React from "react"
import cx from "classnames"
import { useRender } from "@base-ui/react/use-render"
import Icon from "../icon"

// =============================================================================
// Props
// =============================================================================

type PageSecondaryHeaderStepCommonProps = {
  children: React.ReactNode
  className?: string
  /**
   * Whether this step represents the current page.
   */
  isCurrent?: boolean
}

type PageSecondaryHeaderStepLinkProps = PageSecondaryHeaderStepCommonProps &
  React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    /** Step href. Renders an `<a>` when provided. */
    href: string
    /**
     * Replace the rendered element so consumers can supply their own element.
     * Defaults to an `<a>` when `href` is provided, otherwise a `<button>`.
     */
    render?: useRender.RenderProp
  }

type PageSecondaryHeaderStepButtonProps = PageSecondaryHeaderStepCommonProps &
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    /** Step href. Renders a `<button>` when omitted. */
    href?: undefined
    render?: useRender.RenderProp
  }

export type PageSecondaryHeaderStepProps =
  | PageSecondaryHeaderStepLinkProps
  | PageSecondaryHeaderStepButtonProps

// =============================================================================
// Styles
// =============================================================================

const pageSecondaryHeaderStepStyles = [
  "inline-block",
  "outline-2 outline-offset-2 outline-transparent",
  "rounded-sm",
  "py-1 px-2",
  "text-sm font-semibold text-gray-700 leading-6 aria-[current]:bg-gray-100 aria-[current]:text-gray-900",
  "transition-colors duration-200",
  "hover:text-gray-900 focus-visible:outline-gray-900"
]

// =============================================================================
// Component
// =============================================================================

/**
 * Secondary page header step.
 *
 * Renders an `<a>` (when `href` is provided) or a `<button>`, wrapped in an
 * `<li>` with the step divider.
 */
export function PageSecondaryHeaderStep({
  className,
  children,
  isCurrent,
  render,
  ...props
}: PageSecondaryHeaderStepProps) {
  const renderProps =
    props.href !== undefined
      ? {
          defaultTagName: "a" as const,
          props: {
            "aria-current": isCurrent ? ("page" as const) : undefined,
            children,
            className: cx(pageSecondaryHeaderStepStyles, className),
            ...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)
          }
        }
      : {
          defaultTagName: "button" as const,
          props: {
            "aria-current": isCurrent ? ("step" as const) : undefined,
            type: "button" as const,
            children,
            className: cx(pageSecondaryHeaderStepStyles, className),
            ...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)
          }
        }

  const stepElement = useRender({ ...renderProps, render })

  return (
    <li className="flex items-center first-of-type:[&>svg]:hidden">
      <Icon name="right" className="text-gray-700" aria-hidden="true" />
      <div className="flex items-center gap-2">{stepElement}</div>
    </li>
  )
}
